/**
 * Icon Theme Configuration
 *
 * Theme-dependent icon mappings for the Development Theme System.
 * Replaces emoji-based icons in Red Theme with consistent Lucide icons.
 *
 * Both themes now use Lucide React icons for consistency and maintainability.
 *
 * Location: apps/web/src/config/icons/iconThemeConfig.js
 * Created: 2025-01-08
 * Part of: Icon Library Centralization Initiative
 */

import {
  _Mountain as Mountain,
  Compass,
  Activity,
  Footprints,
  Snowflake,
  Ship,
  Tent,
  TreePine,
  Waves,
} from './index.jsx';

/**
 * Red Theme Icon Configuration
 * Previously used emojis - now uses Lucide icons for consistency
 */
export const redThemeIcons = {
  // Brand icon
  brandIcon: Mountain,
  brandIconName: 'Mountain',

  // Category icons (Lucide icons instead of emojis)
  categoryIcons: {
    all: Activity,           // Was: 🏔️
    climbing: Mountain,       // Was: 🧗
    hiking: Footprints,       // Was: 🥾
    skiing: Snowflake,        // Was: ⛷️
    kayaking: Ship,           // Was: 🛶
    camping: Tent,            // Was: ⛺
    mountaineering: TreePine, // Was: ⛰️
    surfing: Waves,           // Was: 🏄
  },
};

/**
 * Emerald Theme Icon Configuration
 * Already uses Lucide icons - maintained for consistency
 */
export const emeraldThemeIcons = {
  // Brand icon
  brandIcon: Compass,
  brandIconName: 'Compass',

  // Category icons (Lucide icons)
  categoryIcons: {
    all: Activity,
    climbing: Mountain,
    hiking: Footprints,
    skiing: Snowflake,
    kayaking: Ship,
    camping: Tent,
    mountaineering: TreePine,
    surfing: Waves,
  },
};

/**
 * Get Theme Icon Configuration
 *
 * Returns icon configuration for the specified theme.
 *
 * @param {string} themeName - Theme identifier ('redTheme' | 'emeraldTheme')
 * @returns {Object} Icon configuration for theme
 *
 * @example
 * const icons = getThemeIcons('emeraldTheme');
 * const BrandIcon = icons.brandIcon;
 * const ClimbingIcon = icons.categoryIcons.climbing;
 */
export function getThemeIcons(themeName) {
  switch (themeName) {
    case 'redTheme':
      return redThemeIcons;
    case 'emeraldTheme':
      return emeraldThemeIcons;
    default:
      console.warn(`[IconThemeConfig] Unknown theme: ${themeName}, defaulting to emeraldTheme`);
      return emeraldThemeIcons;
  }
}

/**
 * Theme Icon Registry
 * Maps theme names to their icon configurations
 */
export const THEME_ICONS = {
  redTheme: redThemeIcons,
  emeraldTheme: emeraldThemeIcons,
};

export default {
  redThemeIcons,
  emeraldThemeIcons,
  getThemeIcons,
  THEME_ICONS,
};
